'use es6';

import { useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { List } from 'foundations-components/transitional/components/List';
import { Button } from 'foundations-components/transitional/components/Button';
import I18n from 'I18n';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { ColumnWrapper } from 'foundations-components/transitional/components/ColumnWrapper';
import { Column } from 'foundations-components/transitional/components/Column';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { PreviewConfigContext } from 'customer-data-sidebar/universal/context/PreviewConfigContext';
import { HostAppCallbacksContext } from 'customer-data-sidebar/universal/context/HostAppCallbacksContext';
import { useDeleteCrmObject } from 'customer-data-sidebar/universal/hooks/useDeleteCrmObject';
import { ConfirmModal } from 'foundations-components/transitional/components/ConfirmModal';
import ObjectTypeIdType from 'customer-data-objects-ui-components/propTypes/ObjectTypeIdType';
import { SidebarSubjectContext } from '../context/SidebarSubjectContext';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { PermissionsContext } from '../context/PermissionsContext';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CrmObjectActionsDropdown = ({
  objectTypeId,
  objectId
}) => {
  const [showModal, setShowModal] = useState(false);
  const [deletePending, setDeletePending] = useState(false);
  const [deleteFailed, setDeleteFailed] = useState(false);
  const {
    definition: {
      singularForm
    },
    primaryDisplayLabel
  } = useContext(SidebarSubjectContext);
  const {
    closeSidebar,
    fromObjectType,
    fromObjectId
  } = useContext(PreviewConfigContext);
  const {
    onObjectDelete
  } = useContext(HostAppCallbacksContext);
  const {
    canEditSubject
  } = useContext(PermissionsContext);
  const handleDeleteSuccess = useCallback(() => {
    setDeletePending(false);
    if (typeof onObjectDelete === 'function') {
      onObjectDelete({
        objectTypeId,
        objectId
      });
    }
    closeSidebar();
  }, [objectTypeId, objectId, closeSidebar, onObjectDelete]);
  const handleDeleteError = useCallback(() => {
    setDeletePending(false);
    setDeleteFailed(true);
  }, [setDeletePending, setDeleteFailed]);
  const handleDeleteCrmObject = useDeleteCrmObject({
    fromObjectType,
    fromObjectId,
    toObjectType: objectTypeId,
    toObjectId: objectId,
    onSuccess: handleDeleteSuccess,
    onError: handleDeleteError
  });
  const handleBeginDelete = useCallback(() => {
    setDeleteFailed(false);
    setDeletePending(true);
    handleDeleteCrmObject();
  }, [setDeleteFailed, setDeletePending, handleDeleteCrmObject]);
  const openModal = useCallback(() => {
    setShowModal(true);
  }, [setShowModal]);
  const closeModal = useCallback(() => {
    setShowModal(false);
  }, [setShowModal]);
  const ConfirmDeleteButton = props => {
    // definitive list of props passed in from UIConfirmModal/UIConfirmDialog here
    // https://git.hubteam.com/HubSpot/UIComponents/blob/4a649019a0e50acaf1273d4eda32581d0f340659/UIComponents/static/js/dialog/UIConfirmDialog.js#L139-L146
    const {
      type,
      autoFocus,
      'data-confirm-button': dataConfirmButton,
      disabled,
      use,
      onClick
    } = props;
    const confirmLabel = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "universalSidebar.crmObjectActionsDropdown.deleteModal.deleteConfirm.object",
      options: {
        singularForm
      }
    });
    return /*#__PURE__*/_jsx(LoadingButton, {
      loading: deletePending,
      failed: deleteFailed,
      type: type,
      autoFocus: autoFocus,
      "data-confirm-button": dataConfirmButton,
      disabled: disabled,
      use: use,
      onClick: onClick,
      "data-test-id": "cobject-delete-confirm-btn",
      children: confirmLabel
    });
  };
  const renderConfirmDeleteModal = () => {
    const message = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "universalSidebar.crmObjectActionsDropdown.deleteModal.deleteTitle.object",
      options: {
        singularForm
      }
    });
    const cancel = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "universalSidebar.crmObjectActionsDropdown.deleteModal.cancel"
    });
    const description = /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: "universalSidebar.crmObjectActionsDropdown.deleteModal.deleteBody.object",
      options: {
        objectLabel: primaryDisplayLabel
      }
    });
    return /*#__PURE__*/_jsx(ConfirmModal, {
      ConfirmButton: ConfirmDeleteButton,
      description: description,
      confirmUse: "danger",
      message: message,
      size: "auto",
      use: "danger",
      "data-selenium-test": "cobject-delete-modal",
      "data-test-id": "cobject-delete-modal",
      onConfirm: handleBeginDelete,
      onReject: closeModal,
      rejectLabel: cancel
    });
  };
  return /*#__PURE__*/_jsxs(TFlex, {
    children: [/*#__PURE__*/_jsxs(ColumnWrapper, {
      align: "center",
      children: [/*#__PURE__*/_jsx(Column, {}), /*#__PURE__*/_jsx(Column, {
        children: /*#__PURE__*/_jsx(Dropdown, {
          menuWidth: 200,
          placement: "bottom right",
          buttonText: I18n.text('universalSidebar.crmObjectActionsDropdown.actions'),
          buttonUse: "transparent",
          "data-selenium-test": "cobject-actions-dropdown",
          "data-test-id": "cobject-actions-dropdown",
          children: /*#__PURE__*/_jsx(List, {
            wrapChildren: true,
            children: /*#__PURE__*/_jsx(Tooltip, {
              disabled: canEditSubject,
              "data-unit-test": "cobject-delete-button-tooltip",
              title: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "universalSidebar.crmObjectActionsDropdown.unauthorizedDelete"
              }),
              children: /*#__PURE__*/_jsx(Button, {
                "data-selenium-test": "cobject-delete-button",
                "data-test-id": "cobject-delete-button",
                onClick: openModal,
                use: "link",
                disabled: !canEditSubject,
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "universalSidebar.crmObjectActionsDropdown.deleteObject"
                })
              })
            })
          })
        })
      })]
    }), showModal && renderConfirmDeleteModal()]
  });
};
CrmObjectActionsDropdown.propTypes = {
  objectTypeId: ObjectTypeIdType.isRequired,
  objectId: PropTypes.string.isRequired
};
export default CrmObjectActionsDropdown;